import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api'
import { ErrorText, MIN_PASSWORD_LENGTH, PasswordHint } from '../components/ui'

export function ForgotPassword() {
  const [email, setEmail] = useState('')

  const request = useMutation({
    mutationFn: () => api.forgotPassword(email.trim()),
  })

  if (request.isSuccess) {
    const configured = request.data.emailConfigured !== false
    return (
      <div className="page" style={{ paddingTop: 'var(--space-7)', minHeight: '100dvh' }}>
        <h1 className="title">{configured ? 'Check your email' : 'Reset by email is off'}</h1>
        {/* When email is on, deliberately the same message whether or not the
            address exists. When it is off, say so rather than promise a link. */}
        <p className="subtitle">{request.data.message}</p>
        {configured ? (
          <p className="faint">The link works once and expires in 45 minutes.</p>
        ) : (
          <div className="banner banner-accent">
            <div>
              <div className="banner-title">Getting back in</div>
              <p className="subtitle">
                On any device already linked to your household, tap your name on the picker and enter the
                household PIN. If this device isn't linked yet, ask another parent for an invite code first.
              </p>
            </div>
          </div>
        )}
        <Link to="/signin" className="btn btn-ghost btn-block">
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
      <h1 className="title" style={{ fontSize: '1.7rem' }}>
        Reset your password
      </h1>
      <p className="subtitle">We'll email you a link to choose a new one.</p>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          request.mutate()
        }}
      >
        <div className="field">
          <label className="label" htmlFor="forgot-email">
            Email
          </label>
          <input
            id="forgot-email"
            className="input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <ErrorText error={request.error} />
        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          disabled={!email.trim() || request.isPending}
        >
          Send the link
        </button>
      </form>

      <Link to="/signin" className="btn btn-quiet btn-block">
        Back
      </Link>
    </div>
  )
}

export function ResetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const reset = useMutation({
    mutationFn: () => api.resetPassword(token, password),
  })

  const mismatch = confirm.length > 0 && confirm !== password

  if (!token) {
    return (
      <div className="page" style={{ paddingTop: 'var(--space-7)', minHeight: '100dvh' }}>
        <h1 className="title">That link is incomplete</h1>
        <p className="subtitle">Open the link from your email again, or request a new one.</p>
        <Link to="/forgot" className="btn btn-primary btn-block">
          Request a new link
        </Link>
      </div>
    )
  }

  if (reset.isSuccess) {
    return (
      <div className="page" style={{ paddingTop: 'var(--space-7)', minHeight: '100dvh' }}>
        <h1 className="title">Password changed</h1>
        <p className="subtitle">
          Every device that was signed in to your account has been signed out.
        </p>
        <button type="button" className="btn btn-primary btn-block btn-lg" onClick={() => navigate('/signin')}>
          Sign in
        </button>
      </div>
    )
  }

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
      <h1 className="title" style={{ fontSize: '1.7rem' }}>
        Choose a new password
      </h1>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          reset.mutate()
        }}
      >
        <div className="field">
          <label className="label" htmlFor="reset-password">
            New password
          </label>
          <input
            id="reset-password"
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <PasswordHint password={password} />
        </div>

        <div className="field">
          <label className="label" htmlFor="reset-confirm">
            Again
          </label>
          <input
            id="reset-confirm"
            className="input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {mismatch ? <span className="error-text">Those don't match.</span> : null}
        </div>

        <ErrorText error={reset.error} />

        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          disabled={password.length < MIN_PASSWORD_LENGTH || mismatch || confirm.length === 0 || reset.isPending}
        >
          Change password
        </button>
      </form>
    </div>
  )
}
